/* eslint-disable react/prop-types */
import { forwardRef, useRef } from "react";

const OtpInput = forwardRef(function OtpInput({ id, label, value = "", onChange, disabled = false, invalid = false, help, digitLength = 6 }, ref) {
    const inputs = useRef([]);
    const digits = Array.from({ length: digitLength }, (_, index) => /^\d$/.test(value[index] || "") ? value[index] : "");
    const description = [help && `${id}-help`, invalid && "auth-feedback"].filter(Boolean).join(" ") || undefined;

    function focus(index) {
        inputs.current[Math.max(0, Math.min(index, digitLength - 1))]?.focus();
    }
    function update(next) {
        // Keep empty positions when editing the middle of a controlled code.
        // Only a complete six-digit value passes the form's submission check.
        onChange(next.map((digit) => digit || " ").join("").trimEnd());
    }
    function insert(index, text) {
        const numeric = text.replace(/\D/g, "").slice(0, digitLength);
        if (!numeric) return;
        const start = numeric.length === digitLength ? 0 : index;
        const next = [...digits];
        for (let offset = 0; offset < numeric.length && start + offset < digitLength; offset++) {
            next[start + offset] = numeric[offset];
        }
        update(next);
        focus(start + numeric.length);
    }
    function clear(index) {
        const next = [...digits];
        next[index] = "";
        update(next);
    }
    function keyDown(event, index) {
        if (event.key === "Backspace") {
            event.preventDefault();
            if (digits[index]) clear(index);
            else focus(index - 1);
        } else if (event.key === "Delete") {
            event.preventDefault();
            clear(index);
        } else if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
            event.preventDefault();
            focus(event.key === "Home" ? 0 : event.key === "End" ? digitLength - 1 : index + (event.key === "ArrowLeft" ? -1 : 1));
        }
    }

    return (
        <div>
            <label id={`${id}-label`} htmlFor={id} className="mb-2 block text-sm font-medium text-zinc-300">{label}</label>
            <div role="group" aria-labelledby={`${id}-label`} className={`grid gap-2 ${digitLength === 4 ? "grid-cols-4" : "grid-cols-6"}`}>
                {digits.map((digit, index) => <input key={index} id={index === 0 ? id : `${id}-${index + 1}`}
                    ref={(element) => {
                        inputs.current[index] = element;
                        if (index === 0) {
                            if (typeof ref === "function") ref(element);
                            else if (ref) ref.current = element;
                        }
                    }}
                    type="text" inputMode="numeric" pattern="[0-9]*" autoComplete={index === 0 ? "one-time-code" : "off"}
                    // Allow a complete autofilled code to reach onChange; the controlled
                    // display still renders one digit in each box.
                    maxLength={digitLength} value={digit} disabled={disabled}
                    aria-label={`Dígito ${index + 1} de ${digitLength}`} aria-invalid={invalid} aria-describedby={description}
                    onFocus={(event) => event.target.select()}
                    onChange={(event) => {
                        if (!event.target.value) clear(index);
                        else insert(index, event.target.value);
                    }}
                    onKeyDown={(event) => keyDown(event, index)}
                    onPaste={(event) => {
                        event.preventDefault();
                        insert(index, event.clipboardData.getData("text"));
                    }}
                    className={`auth-input block h-12 min-w-0 w-full rounded-xl border bg-zinc-900 text-center text-xl font-semibold text-white sm:h-14 ${invalid ? "border-red-400/70" : "border-zinc-700"}`} />)}
            </div>
            {help && <p id={`${id}-help`} className="mt-2 text-xs text-zinc-400">{help}</p>}
        </div>
    );
});

export default OtpInput;
